import React, { useState } from "react";
import axios from "axios";
import { CreditCard, Loader2, BadgeCheck, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { ONLINE_PAYMENTS_ENABLED } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PayOnlineCard = ({ confirmationNumber, email, totalCost, paymentStatus }) => {
  const [busy, setBusy] = useState(false);

  if (paymentStatus === "paid_online") {
    return (
      <div data-testid="paid-online-badge" className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 flex items-center gap-3">
        <BadgeCheck className="text-emerald-600 shrink-0" size={22} />
        <div>
          <div className="font-extrabold text-emerald-700 text-sm">Paid Online</div>
          <div className="text-xs text-emerald-600 mt-0.5">This booking has been paid online. No payment is due at check in.</div>
        </div>
      </div>
    );
  }

  if (!ONLINE_PAYMENTS_ENABLED) return null;

  const base = Number(totalCost);
  const total = Math.round(base * 1.02 * 100) / 100;
  const fee = Math.round((total - base) * 100) / 100;

  const payNow = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/payments/initiate`, {
        confirmation_number: confirmationNumber,
        email,
      });
      const { gateway_url, fields } = res.data;
      const form = document.createElement("form");
      form.method = "POST";
      form.action = gateway_url;
      Object.entries(fields).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = String(v);
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start the online payment. Please try again or pay at check in.");
      setBusy(false);
    }
  };

  return (
    <div data-testid="pay-online-card" className="bg-white border-2 border-[#B5E6EC] rounded-xl p-6">
      <div className="flex items-center gap-2 mb-1">
        <CreditCard size={18} className="text-[#0E9AAA]" />
        <span className="font-extrabold text-[#0A2540]">Pay Online Now</span>
        <Badge className="bg-[#E8F7F9] text-[#0C8695] hover:bg-[#E8F7F9] border border-[#B5E6EC] font-semibold text-[11px]">Optional</Badge>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Skip the line — pay securely online now, or pay by cash, card or cheque at check in.
      </p>
      <div className="bg-[#F6FAFB] rounded-lg p-4 text-sm space-y-1.5 mb-4">
        <div className="flex justify-between text-slate-600"><span>Test fee</span><span className="font-semibold">CI ${base.toFixed(2)}</span></div>
        <div className="flex justify-between text-slate-600"><span>Online transaction fee (2%)</span><span className="font-semibold">CI ${fee.toFixed(2)}</span></div>
        <div className="flex justify-between font-extrabold text-[#0A2540] pt-1.5 border-t border-slate-200"><span>Total online</span><span data-testid="pay-online-total">CI ${total.toFixed(2)}</span></div>
      </div>
      <Button
        data-testid="pay-online-btn"
        onClick={payNow}
        disabled={busy}
        className="w-full bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg h-11 shadow-md"
      >
        {busy ? <><Loader2 size={16} className="mr-2 animate-spin" /> Opening secure payment…</> : <><CreditCard size={16} className="mr-2" /> Pay CI ${total.toFixed(2)} Online</>}
      </Button>
      <p className="text-[11px] text-slate-400 mt-3 flex items-start gap-1.5">
        <ShieldCheck size={13} className="mt-0.5 shrink-0 text-[#0E9AAA]" />
        You will be redirected to our bank's secure payment page (Plug'n Pay via RBC). Your card details are never stored on this website.
      </p>
    </div>
  );
};
